/**
 * TCX-7 — Training Domain-Match Policy Admin (internal-admin surface).
 *
 * Global + provider-scoped domain-match policy management. Route is
 * wrapped in `<InternalAdminRoute>` in App.tsx; this component also
 * relies on `useIsInternalAdmin()` for a defense-in-depth gate mirroring
 * `TrainingCatalogAdmin`.
 *
 * All state comes from the deployed TCX-7 RPCs (via the shared building
 * blocks in `trainingDomainPolicyCommon.tsx`). No server code is touched.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '@/components/layout/TopBar';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useTrainingProviders } from '@/hooks/useTrainingProviders';
import {
  AuditSection,
  CandidatesManager,
  DiagnosticsSection,
  EffectivePolicySection,
  PolicyDisclaimer,
  RulesManager,
} from '@/components/training/trainingDomainPolicyCommon';

export default function TrainingDomainPolicyAdmin() {
  const { data: isInternalAdmin, isLoading } = useIsInternalAdmin();

  if (isLoading) {
    return (
      <div className="container max-w-6xl py-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isInternalAdmin) {
    return (
      <div className="container max-w-6xl py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Not Authorized</h2>
            <p className="text-muted-foreground">
              Training domain-policy administration is restricted to internal
              administrators.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <TrainingDomainPolicyAdminContent />;
}

function TrainingDomainPolicyAdminContent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actor = user?.id ?? null;
  const { data: providers } = useTrainingProviders();

  const [providerId, setProviderId] = useState<string>('');

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 container mx-auto p-4 md:p-6 max-w-6xl pb-20 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-primary" />
              Training Domain Policy
            </h1>
            <p className="text-muted-foreground text-sm">
              Provider-aware, shop-configurable domain-match policy for training
              evidence.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/training-catalog')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Catalog
          </Button>
        </div>

        <PolicyDisclaimer />

        <Tabs defaultValue="global" className="w-full">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="global" data-testid="tdp-tab-global">Global</TabsTrigger>
            <TabsTrigger value="provider" data-testid="tdp-tab-provider">Provider</TabsTrigger>
            <TabsTrigger value="candidates" data-testid="tdp-tab-candidates">Candidates</TabsTrigger>
            <TabsTrigger value="diagnostics" data-testid="tdp-tab-diagnostics">Diagnostics</TabsTrigger>
            <TabsTrigger value="audit" data-testid="tdp-tab-audit">Audit</TabsTrigger>
          </TabsList>

          {/* ── Global scope ─────────────────────────────────────── */}
          <TabsContent value="global" className="space-y-6 pt-4">
            <EffectivePolicySection
              title="Effective global policy"
              description="The system-wide default. Resolved with no provider or shop context."
              resolveShopId={null}
              resolveProviderId={null}
              resolveArtifactId={null}
              writeCtx={{ scope: 'global', providerId: null, shopId: null }}
              actor={actor}
              canWrite
            />
            <RulesManager
              ctx={{ scope: 'global', providerId: null, shopId: null }}
              actor={actor}
              canWrite
              title="Global approved domain rules"
            />
          </TabsContent>

          {/* ── Provider scope ───────────────────────────────────── */}
          <TabsContent value="provider" className="space-y-6 pt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Select a provider</CardTitle>
                <CardDescription>
                  Provider policy overrides the global default for that provider's
                  training.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={providerId} onValueChange={setProviderId}>
                  <SelectTrigger className="w-[320px]" data-testid="tdp-provider-select">
                    <SelectValue placeholder="Choose a provider…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(providers ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.display_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {providerId ? (
              <>
                <EffectivePolicySection
                  title="Effective provider policy"
                  description="Resolved for this provider (inherits from global unless overridden)."
                  resolveShopId={null}
                  resolveProviderId={providerId}
                  resolveArtifactId={null}
                  writeCtx={{ scope: 'provider', providerId, shopId: null }}
                  actor={actor}
                  canWrite
                />
                <RulesManager
                  ctx={{ scope: 'provider', providerId, shopId: null }}
                  actor={actor}
                  canWrite
                  title="Provider approved domain rules"
                />
              </>
            ) : (
              <Card>
                <CardContent className="py-10 text-center text-sm text-muted-foreground">
                  Select a provider to view and manage its policy.
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ── Candidates (all shops) ───────────────────────────── */}
          <TabsContent value="candidates" className="space-y-6 pt-4">
            <CandidatesManager shopId={null} actor={actor} canWrite />
          </TabsContent>

          {/* ── Diagnostics (all shops) ──────────────────────────── */}
          <TabsContent value="diagnostics" className="space-y-6 pt-4">
            <DiagnosticsSection
              shopId={null}
              description="Host mismatches across all shops. Review before enabling strict/allowlist anywhere."
            />
          </TabsContent>

          {/* ── Audit ────────────────────────────────────────────── */}
          <TabsContent value="audit" className="space-y-6 pt-4">
            <AuditSection shopId={null} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
